import { useEffect, useState } from "react";

import { AccountMenu } from "./AccountMenu";
import { fetchCurrentSessionUid, fetchMySessions, revokeSession } from "./auth/authApiLocalClient";
import { getOwnerInfoCached } from "./auth/discoveryClient";
import { Dashboard } from "./Dashboard";
import { OfferPasskeyStep } from "./login/OfferPasskeyStep";
import { PasskeyLoginForm } from "./login/PasskeyLoginForm";
import { WebLoginQr } from "./login/WebLoginQr";
import { SessionsView } from "./SessionsView";

type AuthState = "checking" | "loggedOut" | "loggedIn";
// No react-router here -- one extra screen doesn't earn that yet, same
// state-driven-view pattern App.tsx already uses for authState itself.
type View = "dashboard" | "sessions";

export function App() {
  const [authState, setAuthState] = useState<AuthState>("checking");
  const [ownerName, setOwnerName] = useState<string | null>(null);
  const [view, setView] = useState<View>("dashboard");
  // Set once a QR-confirmed login succeeds -- takes over the login card
  // with the "enroll a passkey" offer before actually moving to the
  // dashboard, same as kwms-web's own LoginPage.
  const [offerPasskeyFor, setOfferPasskeyFor] = useState<{ accessToken: string; phone: string } | null>(null);

  useEffect(() => {
    fetchMySessions().then((sessions) => setAuthState(sessions !== null ? "loggedIn" : "loggedOut"));
  }, []);

  useEffect(() => {
    getOwnerInfoCached()
      .then((info) => setOwnerName(info.owner_name))
      .catch(() => setOwnerName(null));
  }, []);

  // Ends *this device's* own local session server-side (the
  // auth.authusersession row the cookie points at), not just forgets it
  // locally -- otherwise it sits in the DB, still valid until its own 13h
  // expiry. The cookie is HttpOnly (can't read "which session is this"
  // directly), hence the extra round trip via fetchCurrentSessionUid --
  // same reasoning as kwms-web's own logOutEverywhere. Best-effort: a
  // failed revoke (offline, already expired) shouldn't block the UI either
  // way, the app moves to the login screen regardless.
  async function logOut() {
    try {
      const currentSessionUid = await fetchCurrentSessionUid();
      await revokeSession(currentSessionUid);
    } catch {
      // Nothing more to do -- see doc comment above.
    }
    setAuthState("loggedOut");
    setView("dashboard");
  }

  if (authState === "checking") {
    return (
      <div className="app-shell">
        <p className="status-text">Загружаем…</p>
      </div>
    );
  }

  if (authState === "loggedOut") {
    return (
      <div className="app-shell">
        <div className="card">
          <div className="card-header">
            <h1 className="card-title">nodeowner</h1>
            {ownerName && <p className="card-site">{ownerName}</p>}
            <p className="card-subtitle">Войдите, чтобы открыть кабинет</p>
          </div>
          {offerPasskeyFor ? (
            <OfferPasskeyStep
              accessToken={offerPasskeyFor.accessToken}
              phone={offerPasskeyFor.phone}
              onDone={() => setAuthState("loggedIn")}
            />
          ) : (
            <>
              <WebLoginQr onApproved={(accessToken, phone) => setOfferPasskeyFor({ accessToken, phone })} />
              <hr className="divider" />
              <PasskeyLoginForm onLoggedIn={() => setAuthState("loggedIn")} />
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="top-bar">
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {view === "sessions" && (
            <button className="account-menu-trigger" aria-label="Назад" onClick={() => setView("dashboard")}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
          <div className="top-bar-heading">
            <span className="top-bar-title">{view === "sessions" ? "Активные сессии" : "nodeowner"}</span>
            {ownerName && <span className="top-bar-site">{ownerName}</span>}
          </div>
        </div>
        <AccountMenu onOpenSessions={() => setView("sessions")} onLogOut={logOut} />
      </header>
      {view === "sessions" ? <SessionsView onLogOut={logOut} /> : <Dashboard />}
    </div>
  );
}
